import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/use-auth';
import { AuthButton } from '../auth-button';
import { AuthTokenModal } from '../auth-token-modal';
import { UserSwitcher } from '../user-switcher';
import { DivisionSelector } from './division-selector';
import { useHelp } from '../../contexts/HelpContext';
import { useTheme } from '../../contexts/theme-context';
import { User, LogOut, ChevronDown, Eye, HelpCircle, Sun, Moon } from 'lucide-react';

export function TopNavigation() {
  const { isAuthenticated, user, tokenClaims, signOut } = useAuth();
  // given_name/family_name come from the OIDC `profile` scope (ID token / userinfo),
  // not the access token, so prefer user.profile over tokenClaims.
  const givenName = user?.profile?.given_name || tokenClaims?.given_name;
  const familyName = user?.profile?.family_name || tokenClaims?.family_name;
  const email = user?.profile?.email || tokenClaims?.email;
  const { openHelpPanel } = useHelp();
  const { theme, toggleTheme } = useTheme();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isTokenModalOpen, setIsTokenModalOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className="bg-white dark:bg-secondary-800 border-b border-gray-200 dark:border-secondary-700 px-6 py-4">
      <div className="flex justify-between items-center">
        {/* Left side - could be used for breadcrumbs or page title */}
        <div className="flex-1">
          {/* Empty for now, could add breadcrumbs here */}
        </div>

        {/* Right side - User info and auth controls */}
        <div className="flex items-center space-x-4">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            className="flex items-center hover:bg-gray-100 dark:hover:bg-secondary-600 dark:hover:bg-secondary-700 rounded-lg p-2 transition-colors text-gray-600 dark:text-secondary-400 dark:text-secondary-300 hover:text-gray-900 dark:hover:text-white"
          >
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>

          {isAuthenticated && tokenClaims && (
            <>
              {/* Division Selector */}
              <DivisionSelector />
              
              {/* User Switcher */}
              <UserSwitcher />
              
              {/* Help Controls */}
              <div className="flex items-center border-l border-gray-200 dark:border-secondary-700 pl-4 ml-4">
                {/* Help Panel Button */}
                <button
                  onClick={() => openHelpPanel()}
                  className="flex items-center space-x-2 hover:bg-gray-100 dark:hover:bg-secondary-600 dark:hover:bg-secondary-700 rounded-lg px-3 py-2 transition-colors text-gray-600 dark:text-secondary-400 dark:text-secondary-300 hover:text-gray-900 dark:hover:text-white"
                  title="Open help center"
                >
                  <HelpCircle className="h-5 w-5" />
                  <span className="text-sm font-medium hidden md:inline">Help</span>
                </button>
              </div>
              
              {/* User Menu */}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-3 hover:bg-gray-50 dark:hover:bg-secondary-700 rounded-lg px-3 py-2 transition-colors"
                >
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-700 dark:text-secondary-200">
                      {givenName} {familyName}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-secondary-400">{email}</p>
                  </div>
                  <div className="h-8 w-8 bg-gray-200 dark:bg-secondary-700 dark:bg-secondary-600 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-gray-600 dark:text-secondary-400 dark:text-secondary-200">
                      {givenName?.[0]}{familyName?.[0]}
                    </span>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-gray-500 dark:text-secondary-400 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Dropdown Menu */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-secondary-800 rounded-lg shadow-lg border border-gray-200 dark:border-secondary-700 py-1 z-50">
                    <Link
                      to="/profile"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-secondary-200 hover:bg-gray-50 dark:hover:bg-secondary-700 transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <User className="h-4 w-4 mr-3 text-gray-500 dark:text-secondary-400" />
                      My Profile
                    </Link>
                    <button
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        setIsTokenModalOpen(true);
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-secondary-200 hover:bg-gray-50 dark:hover:bg-secondary-700 transition-colors"
                    >
                      <Eye className="h-4 w-4 mr-3 text-gray-500 dark:text-secondary-400" />
                      View Token
                    </button>
                    <div className="border-t border-gray-200 dark:border-secondary-700 my-1"></div>
                    <button
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        signOut();
                      }}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
                    >
                      <LogOut className="h-4 w-4 mr-3" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Show auth button if not authenticated */}
          {!isAuthenticated && (
            <AuthButton />
          )}
        </div>
      </div>

      {/* Token Modal */}
      <AuthTokenModal
        isOpen={isTokenModalOpen}
        onClose={() => setIsTokenModalOpen(false)}
      />
    </header>
  );
}